/**
 * useRoomSync — binds the video.js player to the server-authoritative sync
 * state over the typed socket (WS-8). See §WS-8 and §5.3 (frozen signature).
 *
 * Handles room:join on mount, room:state / sync:apply / track:* / room:* /
 * error; projects authoritative state to server-now and applies
 * computeCorrection (soft rate nudge reverted after SYNC.SOFT_WINDOW_MS, else
 * hard seek); guards programmatic play/pause/seek/rate with a 1s failsafe so
 * server-driven changes never echo as host actions (#1 feedback loop); surfaces
 * autoplay NotAllowedError via needsUserGesture; emits host sync:* events on
 * non-programmatic player events (host only); reconnects (re-join + clock
 * resync) and shows Reconnecting… after SYNC.STALE_STATE_MS; and emits
 * player.trigger('syncstatus', payload) for WS-7's SyncStatusIndicator.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  SYNC,
  projectPosition,
  type PlaybackState,
  type Role,
  type RoomStatePayload,
  type SyncApplyPayload,
  type TrackAudioBroadcast,
  type TrackSubtitleBroadcast,
  type SocketErrorPayload,
} from '@syncreel/shared';
import type { Player } from '../player/useVideoJs';
import { getSocket, type SyncSocket } from '../socket/socket';
import { createClock, type ClockController } from '../lib/clock';
import { onViewerReturn } from '../lib/returnTriggers';
import { computeCorrection } from '../lib/syncEngine';
import { useSeekGuard } from './useSeekGuard';
import { logger } from '../api/logger';

/** Payload emitted on the player's `syncstatus` custom event. WS-7's
 * SyncStatusIndicator listens to the same event name (`'syncstatus'`) and
 * consumes this shape — see DONE-WS-8.md. */
export interface SyncStatusPayload {
  status: 'in-sync' | 'adjusting' | 'reconnecting';
  driftSec?: number;
}

// WHY: video.js #3936 — a player paused at exactly currentTime === 0 never
// fires `seeking`, so we never hard-seek to exactly 0; nudge by a sub-ms
// epsilon that is invisible and inside IGNORE_BAND_S.
const SEEKBOT_SEED_S = 0.001;
// WHY: failsafe for the isProgrammatic guard — if the matching player event
// never fires, clear the flag after this so a later user action is not
// mistaken for a programmatic one.
const PROGRAMMATIC_FAILSAFE_MS = 1000;

/**
 * Bind `player` to the server-authoritative sync state for `roomId`.
 * @param args.roomId  room to join.
 * @param args.player  video.js player (null until ready; re-binds on ready).
 * @param args.role    current role; host actions are gated on 'host'.
 */
export function useRoomSync(args: {
  roomId: string;
  player: Player | null;
  role: Role;
}): {
  state: PlaybackState | null;
  drift: number;
  connected: boolean;
  needsUserGesture: boolean;
  hostActions: {
    play(): void;
    pause(): void;
    seek(s: number): void;
    setRate(r: number): void;
  };
  acknowledgeGesture(): void;
  /** Re-join and re-sample the clock so the server re-sends authoritative state. */
  resync(): void;
} {
  const { roomId, player, role } = args;
  const [state, setState] = useState<PlaybackState | null>(null);
  const [drift, setDrift] = useState(0);
  const [connected, setConnected] = useState(false);
  const [needsUserGesture, setNeedsUserGesture] = useState(false);

  const playerRef = useRef<Player | null>(player);
  playerRef.current = player;
  const programmaticRef = useRef(false);
  const failsafeRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef<PlaybackState | null>(null);
  const clockRef = useRef<ClockController | null>(null);
  const socketRef = useRef<SyncSocket | null>(null);
  const softRevertRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSyncApplyRef = useRef<number>(0);
  const stalePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resyncRef = useRef<(() => void) | null>(null);

  const getAuthoritativePosition = useCallback((): number => {
    const st = stateRef.current;
    if (!st) return 0;
    return projectPosition(st, clockRef.current?.serverNow() ?? Date.now());
  }, []);

  // WHY: the seek guard shares programmaticRef + the live authoritative
  // position so server-driven (programmatic) seeks are not re-snapped.
  useSeekGuard({ player, role, programmaticRef, getAuthoritativePosition });

  const setProgrammatic = useCallback((): void => {
    programmaticRef.current = true;
    if (failsafeRef.current !== null) clearTimeout(failsafeRef.current);
    failsafeRef.current = setTimeout(() => {
      programmaticRef.current = false;
    }, PROGRAMMATIC_FAILSAFE_MS);
  }, []);

  const clearProgrammatic = useCallback((): void => {
    if (failsafeRef.current !== null) {
      clearTimeout(failsafeRef.current);
      failsafeRef.current = null;
    }
    programmaticRef.current = false;
  }, []);

  const triggerSyncStatus = useCallback(
    (status: SyncStatusPayload['status'], driftSec?: number): void => {
      const p = playerRef.current;
      if (!p) return;
      const payload: SyncStatusPayload =
        driftSec !== undefined ? { status, driftSec } : { status };
      p.trigger('syncstatus', payload);
    },
    [],
  );

  const handleAutoplay = useCallback((e: unknown): void => {
    // WHY: browsers block autoplay without a gesture; surface NotAllowedError
    // so WS-7's JoinPlaybackOverlay can prompt. Other failures are noise.
    if (e instanceof DOMException && e.name === 'NotAllowedError') {
      setNeedsUserGesture(true);
    } else {
      logger.warn('player.play() rejected', e);
    }
  }, []);

  const applyState = useCallback(
    (next: PlaybackState): void => {
      const p = playerRef.current;
      if (!p) return;
      stateRef.current = next;
      setState(next);
      lastSyncApplyRef.current = Date.now();

      const expected = projectPosition(next, clockRef.current?.serverNow() ?? Date.now());
      const actual = p.currentTime() ?? 0;
      const driftSec = expected - actual;
      setDrift(driftSec);

      const correction = computeCorrection({
        expected,
        actual,
        baseRate: next.rate,
        isPlaying: next.isPlaying,
      });
      if (softRevertRef.current !== null) {
        clearTimeout(softRevertRef.current);
        softRevertRef.current = null;
      }

      if (correction.kind === 'none') {
        triggerSyncStatus('in-sync', driftSec);
      } else if (correction.kind === 'soft') {
        triggerSyncStatus('adjusting', driftSec);
        setProgrammatic();
        p.playbackRate(correction.rate);
        // WHY: revert to base rate after the nudge window so the player does
        // not run fast/slow indefinitely.
        softRevertRef.current = setTimeout(() => {
          setProgrammatic();
          p.playbackRate(next.rate);
        }, correction.windowMs);
      } else {
        triggerSyncStatus('adjusting', driftSec);
        setProgrammatic();
        // WHY: #3936 — never hard-seek to exactly 0; seed a non-zero baseline.
        const target = correction.position === 0 ? SEEKBOT_SEED_S : correction.position;
        p.currentTime(target);
      }

      // Align play/pause to the authority regardless of correction kind.
      if (next.isPlaying && p.paused()) {
        setProgrammatic();
        const pr = p.play();
        if (pr) pr.catch(handleAutoplay);
      } else if (!next.isPlaying && !p.paused()) {
        setProgrammatic();
        p.pause();
      }
    },
    [handleAutoplay, setProgrammatic, triggerSyncStatus],
  );

  useEffect(() => {
    if (!player) return;
    playerRef.current = player;
    let socket: SyncSocket;
    try {
      socket = getSocket(roomId);
    } catch (err) {
      logger.warn('useRoomSync: cannot open socket', err);
      return;
    }
    socketRef.current = socket;
    const clock = createClock(socket);
    clock.start();
    clockRef.current = clock;
    socket.emit('room:join', { roomId });
    setConnected(socket.connected);
    lastSyncApplyRef.current = Date.now();
    // WHY: if the socket is already connected (e.g. reused from a previous
    // mount), onConnect won't fire again, so trigger in-sync here. The
    // server's room:state response will refine this via applyState.
    if (socket.connected) {
      triggerSyncStatus('in-sync');
    } else {
      triggerSyncStatus('reconnecting');
    }

    const onRoomState = (payload: RoomStatePayload): void => {
      applyState(payload.state);
    };
    const onSyncApply = (payload: SyncApplyPayload): void => {
      applyState(payload);
    };
    const onTrackAudio = (payload: TrackAudioBroadcast): void => {
      // WHY: audio track selection is per-viewer (like subtitle/audio delay).
      // The host already changed it locally via video.js; viewers choose
      // their own track via the built-in audioTrackButton. We absorb the
      // event so it is not logged as unhandled.
      void payload;
    };
    const onTrackSubtitle = (payload: TrackSubtitleBroadcast): void => {
      const p = playerRef.current;
      if (!p) return;
      const list = p.textTracks?.() as unknown as {
        length: number;
        [i: number]: { id: string; kind: string; mode: string } | undefined;
      };
      if (!list) return;
      for (let i = 0; i < list.length; i++) {
        const t = list[i];
        if (!t) continue;
        if (t.kind === 'subtitles' || t.kind === 'captions') {
          t.mode = t.id === payload.trackId ? 'showing' : 'disabled';
        }
      }
    };
    // WHY: member-list rendering + role changes are WS-5's job (store updates
    // on these events); we absorb them so they are not unhandled, and the
    // role prop change re-binds host handlers via this effect's deps.
    const onMembers = (): void => {};
    const onHostChanged = (): void => {};
    const onError = (payload: SocketErrorPayload): void => {
      logger.warn('socket error', payload.code, payload.message);
    };

    socket.on('room:state', onRoomState);
    socket.on('sync:apply', onSyncApply);
    socket.on('track:audio', onTrackAudio);
    socket.on('track:subtitle', onTrackSubtitle);
    socket.on('room:members', onMembers);
    socket.on('room:host-changed', onHostChanged);
    socket.on('error', onError);

    const onConnect = (): void => {
      setConnected(true);
      // WHY: on reconnect the server has fresh state; re-join and re-sample
      // the clock offset so projectPosition is accurate again.
      socket.emit('room:join', { roomId });
      clock.resync();
      lastSyncApplyRef.current = Date.now();
      // WHY: reset to in-sync on connect; the server will send room:state
      // which may update this to adjusting if a correction is needed.
      triggerSyncStatus('in-sync');
    };
    const onDisconnect = (): void => {
      setConnected(false);
      triggerSyncStatus('reconnecting');
    };
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    const isHost = role === 'host';
    const onPlay = (): void => {
      if (programmaticRef.current) return clearProgrammatic();
      if (isHost) socket.emit('sync:play', { roomId, position: player.currentTime() ?? 0 });
    };
    const onPause = (): void => {
      if (programmaticRef.current) return clearProgrammatic();
      if (isHost) socket.emit('sync:pause', { roomId, position: player.currentTime() ?? 0 });
    };
    const onSeeked = (): void => {
      if (programmaticRef.current) return clearProgrammatic();
      if (isHost) socket.emit('sync:seek', { roomId, position: player.currentTime() ?? 0 });
    };
    const onRatechange = (): void => {
      if (programmaticRef.current) return clearProgrammatic();
      if (isHost) socket.emit('sync:rate', { roomId, rate: player.playbackRate() as 0.5 | 0.75 | 1 | 1.25 | 1.5 | 2 });
    };
    player.on('play', onPlay);
    player.on('pause', onPause);
    player.on('seeked', onSeeked);
    player.on('ratechange', onRatechange);

    // WHY: with no sync:apply for STALE_STATE_MS show Reconnecting… even if
    // the socket is technically open (silent room / no heartbeats).
    stalePollRef.current = setInterval(() => {
      if (Date.now() - lastSyncApplyRef.current > SYNC.STALE_STATE_MS) {
        triggerSyncStatus('reconnecting');
      }
    }, 1000);

    // WHY: a hidden tab / locked phone / dropped network leaves the player
    // behind, and a paused room sends no heartbeats to correct it. On return,
    // re-join so the server replies with fresh room:state (a reconnecting
    // socket does this itself in onConnect).
    const resync = (): void => {
      if (!socket.connected) {
        socket.connect();
        return;
      }
      socket.emit('room:join', { roomId });
      clock.resync();
    };
    resyncRef.current = resync;
    const offReturn = onViewerReturn(resync);

    return () => {
      offReturn();
      resyncRef.current = null;
      socket.off('room:state', onRoomState);
      socket.off('sync:apply', onSyncApply);
      socket.off('track:audio', onTrackAudio);
      socket.off('track:subtitle', onTrackSubtitle);
      socket.off('room:members', onMembers);
      socket.off('room:host-changed', onHostChanged);
      socket.off('error', onError);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      player.off('play', onPlay);
      player.off('pause', onPause);
      player.off('seeked', onSeeked);
      player.off('ratechange', onRatechange);
      clock.stop();
      clockRef.current = null;
      socketRef.current = null;
      if (softRevertRef.current !== null) {
        clearTimeout(softRevertRef.current);
        softRevertRef.current = null;
      }
      if (stalePollRef.current !== null) {
        clearInterval(stalePollRef.current);
        stalePollRef.current = null;
      }
      if (failsafeRef.current !== null) {
        clearTimeout(failsafeRef.current);
        failsafeRef.current = null;
      }
      // WHY: do not disconnect the socket — WS-5 owns the singleton lifecycle.
    };
  }, [roomId, player, role, applyState, clearProgrammatic, triggerSyncStatus]);

  const hostActions = useMemo(
    () => ({
      play(): void {
        if (role !== 'host') return;
        const p = playerRef.current;
        if (!p) return;
        setProgrammatic();
        const pr = p.play();
        if (pr) pr.catch(handleAutoplay);
        socketRef.current?.emit('sync:play', { roomId, position: p.currentTime() ?? 0 });
      },
      pause(): void {
        if (role !== 'host') return;
        const p = playerRef.current;
        if (!p) return;
        setProgrammatic();
        p.pause();
        socketRef.current?.emit('sync:pause', { roomId, position: p.currentTime() ?? 0 });
      },
      seek(s: number): void {
        if (role !== 'host') return;
        const p = playerRef.current;
        if (!p) return;
        setProgrammatic();
        p.currentTime(s);
        socketRef.current?.emit('sync:seek', { roomId, position: s });
      },
      setRate(r: number): void {
        if (role !== 'host') return;
        const p = playerRef.current;
        if (!p) return;
        setProgrammatic();
        p.playbackRate(r);
        socketRef.current?.emit('sync:rate', { roomId, rate: r as 0.5 | 0.75 | 1 | 1.25 | 1.5 | 2 });
      },
    }),
    [role, roomId, setProgrammatic, handleAutoplay],
  );

  const acknowledgeGesture = useCallback((): void => {
    setNeedsUserGesture(false);
    const p = playerRef.current;
    if (!p) return;
    // WHY: the click is the gesture that unblocks autoplay; re-attempt play
    // and let the sync engine keep the position aligned.
    setProgrammatic();
    const pr = p.play();
    if (pr) pr.catch(handleAutoplay);
  }, [handleAutoplay, setProgrammatic]);

  const resync = useCallback((): void => {
    resyncRef.current?.();
  }, []);

  return { state, drift, connected, needsUserGesture, hostActions, acknowledgeGesture, resync };
}
